import React from "react";
import { SheetOverlay, CopyButton } from "./styles.payouts.js";
import { Pill, TagRow } from "./styles.paymentDetails.js";
import {
  Sheet,
  SheetHead,
  SheetTitle,
  CloseButton,
  SheetBody,
  DetailCard,
  TitleBar,
  DetailTitle,
  Estimated,
  Details,
  DetailRow,
  DetailLabel,
  DetailValue,
  BottomMessage,
} from "./styles.transactionDetail.js";
import {
  isValidDate,
  formatFullDate,
  formatMoney,
  copyToClipboard,
  CopyIcon,
  withDividers,
} from "./payoutsFormat.js";

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M1 1L13 13M13 1L1 13"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

// Share of the base amount, e.g. 118.8 of 3960 -> "3%".
const formatPercent = (part, base) =>
  base ? `${Number(((Math.abs(part) / Math.abs(base)) * 100).toFixed(2))}%` : "";

// "CGST (3%)", or just "CGST" when there is no base to take a share of.
const percentLabel = (name, part, base) => {
  const percent = formatPercent(part, base);
  return percent ? `${name} (${percent})` : name;
};

const line = (key, label, value, extra) => (
  <DetailRow key={key}>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>
      {value}
      {extra}
    </DetailValue>
  </DetailRow>
);

// Everything is derived from the transaction row already on the list, no extra api call.
// Shared by the mobile bottom sheet and the desktop drawer.
export const TransactionDetailContent = ({ item }) => {
  const tag = item.tag;
  const isHold = (tag || "").toLowerCase() === "hold";
  const identifier = item.transaction_identifier || {};
  const typeLabel = item.display_transaction_type;

  const transactionNumber = item.transaction_number;
  const parentNumber = item.parent_transaction_number;
  // Same number as its parent -> it is the GRN itself, otherwise a note linked to a GRN.
  const isLinked = Boolean(parentNumber) && parentNumber !== transactionNumber;

  const isEstimated =
    (item.transaction_type || "").toUpperCase() === "GRN_COMPLETE" &&
    (item.transaction_status || "").toUpperCase() !== "SUCCESS";

  const grnDoneOn = item.metadata?.trade_metadata?.grn_completed_at;
  const paidOn = item.utr_date;
  const reason = item.comment;

  const topRows = [];
  const identifierLabel = identifier.display_type || identifier.type;
  if (identifierLabel && identifier.value) {
    topRows.push(line("identifier", identifierLabel, identifier.value));
  }
  if (transactionNumber) {
    topRows.push(line("number", "Transaction number", transactionNumber));
  }
  if (parentNumber) {
    topRows.push(line("grn", isLinked ? "Linked GRN" : "GRN", parentNumber));
  }
  if (isLinked) {
    if (isValidDate(item.created_at)) {
      topRows.push(line("created", "Created on", formatFullDate(item.created_at)));
    }
  } else if (isValidDate(grnDoneOn)) {
    topRows.push(line("grn-done", "GRN done on", formatFullDate(grnDoneOn)));
  }
  if (isValidDate(item.consideration_date)) {
    topRows.push(
      line("due", "Payment due", formatFullDate(item.consideration_date))
    );
  }
  if (reason) {
    topRows.push(line("reason", isHold ? "Hold reason" : "Reason", reason));
  }
  if (item.utr_number) {
    topRows.push(
      line(
        "utr",
        "UTR",
        item.utr_number,
        <CopyButton
          type="button"
          onClick={() => copyToClipboard(item.utr_number)}
          aria-label="Copy UTR"
        >
          <CopyIcon />
        </CopyButton>
      )
    );
  }
  if (isValidDate(paidOn)) {
    topRows.push(line("paid-on", "Paid on", formatFullDate(paidOn)));
  }

  const subtotal = Number(item.subtotal || 0);
  const breakdownRows = [
    line("total", "Total amount", formatMoney(subtotal)),
  ];
  [
    ["CGST", item.cgst_amount],
    ["SGST", item.sgst_amount],
    ["IGST", item.igst_amount],
  ].forEach(([name, amount]) => {
    if (Number(amount || 0)) {
      breakdownRows.push(
        line(
          name,
          percentLabel(name, amount, subtotal),
          formatMoney(Number(amount))
        )
      );
    }
  });
  if (Number(item.tds_amount || 0)) {
    breakdownRows.push(
      line(
        "tds",
        percentLabel("TDS", item.tds_amount, subtotal),
        formatMoney(Number(item.tds_amount))
      )
    );
  }

  const sections = [
    <React.Fragment key="top">{withDividers(topRows)}</React.Fragment>,
    <Details as="div" key="breakdown" style={{ padding: 0 }}>
      {breakdownRows}
    </Details>,
    line("net", "Net payable", formatMoney(Number(item.transaction_amount || 0))),
  ];
  if (item.bottom_message) {
    sections.push(
      <BottomMessage key="message">{item.bottom_message}</BottomMessage>
    );
  }

  return (
    <DetailCard>
      {tag ? (
        <TagRow>
          <Pill status={tag}>{tag}</Pill>
        </TagRow>
      ) : null}
      <TitleBar hasTag={Boolean(tag)}>
        <DetailTitle>
          {typeLabel ? `${typeLabel} • ` : ""}
          {formatMoney(Number(item.transaction_amount || 0))}
          {isEstimated ? <Estimated> (estimated)</Estimated> : null}
        </DetailTitle>
      </TitleBar>
      <Details>{withDividers(sections)}</Details>
    </DetailCard>
  );
};

const TransactionDetailSheet = ({ item, onClose }) => (
  <SheetOverlay onClick={onClose}>
    <Sheet onClick={(e) => e.stopPropagation()}>
      <SheetHead>
        <SheetTitle>Transaction detail</SheetTitle>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </CloseButton>
      </SheetHead>
      <SheetBody>
        <TransactionDetailContent item={item} />
      </SheetBody>
    </Sheet>
  </SheetOverlay>
);

export default TransactionDetailSheet;
